// Who is signed in, their profile and role. Roles: viewer < planner < admin. A collector is
// apart: no planning pages, only the Collect page, where they can mark pickups done.
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AuthUser, Profile, Role } from '../domain/types';
import { clearAppointmentCache } from '../lib/appointments';
import { db } from '../lib/db';

export const ROLE_RANK: Record<Role, number> = { collector: 0, viewer: 1, planner: 2, admin: 3 };

export type AuthStatus = 'loading' | 'signedOut' | 'pending' | 'ready' | 'error';

export interface Auth {
  status: AuthStatus;
  user: AuthUser | null;
  profile: Profile | null;
  error: string | null;
  canWrite: boolean;
  isAdmin: boolean;
  /** a collector: only the Collect page */
  isCollector: boolean;
  /** may change appointments in the UCO app (collector, planner, admin) */
  canCollect: boolean;
  hasRole(role: Role): boolean;
  refresh(): Promise<void>;
  signOut(): Promise<void>;
}

const Ctx = createContext<Auth | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const u = await db.getUser();
      if (!u) {
        setUser(null);
        setProfile(null);
        setStatus('signedOut');
        return;
      }
      const p = (await db.myProfile(u.id)) || { id: u.id, email: u.email, full_name: '', role: 'viewer' as Role, approved: false };
      setUser(u);
      setProfile(p);
      setError(null);
      setStatus(p.approved ? 'ready' : 'pending');
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : String(e));
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    void refresh();
    return db.onAuth((ev) => {
      if (ev === 'SIGNED_IN' || ev === 'SIGNED_OUT' || ev === 'USER_UPDATED') void refresh();
    });
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      if (user) await clearAppointmentCache(user.id);
    } catch {
      /* the cache is only a convenience */
    }
    await db.signOut();
    qc.clear();
    await refresh();
  }, [user, qc, refresh]);

  const value = useMemo<Auth>(() => {
    const rank = profile && status === 'ready' ? ROLE_RANK[profile.role] || 0 : 0;
    return {
      status, user, profile, error,
      canWrite: rank >= ROLE_RANK.planner,
      isAdmin: rank >= ROLE_RANK.admin,
      isCollector: status === 'ready' && profile?.role === 'collector',
      canCollect: status === 'ready' && (profile?.role === 'collector' || rank >= ROLE_RANK.planner),
      hasRole: (r) => rank >= ROLE_RANK[r],
      refresh, signOut,
    };
  }, [status, user, profile, error, refresh, signOut]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): Auth {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
